"use client";

import React, { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { ImageCropModal } from "@/components/ui/ImageCropModal";

import {
  MapPin,
  Bed,
  Bath,
  Square,
  Phone,
  Mail,
  Upload,
  Image as ImageIcon,
  Zap,
  Crop,
  X,
  Tag,
  Home,
  Calendar,
  Building,
  Car,
  Ruler,
  Check,
  Loader2,
  Trash2,
  Building2,
  IdCard
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import useTemplateBase, {
  mergePropertyListsWithCurrent,
  resolvePropertyListName,
} from "./TemplateBase";
import axiosInstance from "@/config/axiosInstance";
import { endpoints } from "@/config/apiEndpoints";
import openHouseBadge from "../../assets/star_red.png";

interface Property {
  id: number;
  propertyListType?: {
    id: number;
    list_name: string;
    custom_template_data?: any;
  };
  title: string;
  listing_id: string;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  price?: number | null;
  bedrooms?: number | null;
  bathrooms?: number | null;
  area?: number | null;
  description?: string | null;
  text_section?: string | null;
  image?: string | null;
  original_image?: string | null;
  photos?: string[] | null;
  // Keyed by slot name (e.g. "photo2"), not array position — see TemplateBase.tsx's updatePhotoAtIndex.
  original_photos?: Record<string, string> | null;
  is_open_house?: boolean;
}

interface PropertyList {
  id: number;
  list_name: string;
}

interface KeyFeature {
  id?: number;
  feature_name: string;
  propetyId: number;
}

interface Template7Props {
  property?: Property | null;
  propertyLists?: PropertyList[];
  keyFeatures?: KeyFeature[];
  onDirtyChange?: (dirty: boolean) => void;
  onChange?: (updatedFields: Partial<Template7FlyerData>) => void;
  onPropertyListChange?: (selectedList: string) => void;
  selectedPropertyList?: string;
  onPropertyListCreated?: (newList: PropertyList) => void;
  selectedList?: string; // ✅ ADD THIS PROP - same as other templates
  readOnly?: boolean; // ✅ ADD THIS PROP - same as other templates
}

interface FeatureItem {
  id?: number;
  feature_name: string;
}

interface Template7FlyerData {
  tag: string;
  price: number | null;
  headline: string;
  address: string;
  bedrooms: number | null;
  bathrooms: number | null;
  sqft: number | null;
  description: string;
  features: FeatureItem[];
  agentName?: string;
  agentPhone?: string;
  agentEmail?: string;
  companyName?: string;
  heroImage: string;
  photo2?: string;
  photo3?: string;
  photo4?: string;
  list_name?: string;
}

// ─── Helper: route already-saved (non data:/blob:) S3 image URLs through the
// backend image proxy before feeding them to the crop modal's <canvas>, so the
// export doesn't fail with a cross-origin "tainted canvas" error. Mirrors the
// same helper in Template1.tsx.
const getEditableImageUrl = (url: string): string => {
  if (!url || url.startsWith("data:") || url.startsWith("blob:")) return url;
  try {
    const parsed = new URL(url, window.location.origin);
    const needsProxy =
      parsed.hostname.includes("amazonaws.com") ||
      parsed.hostname.includes("mylistiq-production.s3");
    if (!needsProxy) return url;
    return `${endpoints.image_proxy}?url=${encodeURIComponent(url)}`;
  } catch {
    return url;
  }
};

const Template7: React.FC<Template7Props> = ({
  property,
  propertyLists = [],
  keyFeatures = [],
  onDirtyChange,
  onChange,
  onPropertyListChange,
  selectedPropertyList: externalSelectedList,
  onPropertyListCreated,
  selectedList: viewSelectedList, // ✅ ADD THIS - same as other templates
  readOnly = false, // ✅ ADD THIS - same as other templates
}) => {
  const { toast } = useToast();

  const [cropModal, setCropModal] = useState<{ open: boolean; imageUrl: string }>({
    open: false,
    imageUrl: "",
  });

  // Tracks the untouched "original" hero photo for this editing session, so
  // Edit Image always re-crops from the full source rather than whatever is
  // currently displayed. Mirrors the same state in Template1.tsx.
  const [originalHeroImage, setOriginalHeroImage] = useState<string>(
    property?.original_image || ""
  );

  const photo2Ref = useRef<HTMLInputElement>(null);
  const photo3Ref = useRef<HTMLInputElement>(null);
  const photo4Ref = useRef<HTMLInputElement>(null);
  // Container refs + resolution hints (like Template4)
  const heroContainerRef = useRef<HTMLDivElement | null>(null);
  const secondaryContainerRef = useRef<HTMLDivElement | null>(null);
  const [boxHeroRes, setBoxHeroRes] = useState<string | null>(null);
  const [boxSecondaryRes, setBoxSecondaryRes] = useState<string | null>(null);
  const RESOLUTION_HINT = "For best results, upload an image matching the box resolution.";

  // Custom list creation state
  const [isCustomList, setIsCustomList] = useState<boolean>(false);
  const [customListName, setCustomListName] = useState<string>("");
  const [isSavingCustomList, setIsSavingCustomList] = useState(false);
  const [localPropertyLists, setLocalPropertyLists] =
    useState<PropertyList[]>(propertyLists);
  const [localSelectedListOverride, setLocalSelectedListOverride] = useState<
    string | null
  >(null);

  // Custom fields editor state
  const [editingCustom7, setEditingCustom7] = useState(false);
  const [customFields7, setCustomFields7] = useState<
    { key: string; value: string }[]
  >([]);

  // ✅ USE TEMPLATE BASE HOOK - SAME AS TEMPLATE1
  const {
    flyerData,
    setFlyerData,
    selectedPropertyList,
    fileInputRef,
    agentData,
    handleInputChange,
    addFeature,
    handleFeatureChange,
    handleFeatureBlur,
    handleFeatureDelete,
    updatePropertyList,
    updatePhotoAtIndex,
    uploadCroppedPhoto,
    saveCustomTemplateData,
  } = useTemplateBase({
    property,
    propertyLists: localPropertyLists,
    keyFeatures,
    selectedPropertyList: externalSelectedList,
    onDirtyChange,
    onChange,
    onPropertyListChange,
  });

  // Sync local property lists when prop changes
  useEffect(() => {
    setLocalPropertyLists(propertyLists);
  }, [propertyLists]);

  // ✅ Reset local override when property changes
  useEffect(() => {
    setLocalSelectedListOverride(null);
  }, [property?.id]);

  useEffect(() => {
    setOriginalHeroImage(property?.original_image || "");
  }, [property?.id]);

  // Initialize custom fields from property
  useEffect(() => {
    const existingCustomData =
      property?.propertyListType?.custom_template_data?.template7 || [];
    setCustomFields7(
      Array.isArray(existingCustomData) ? existingCustomData : []
    );
  }, [property?.propertyListType?.custom_template_data?.template7]);

  // ✅ FIX: Determine currently selected list - SAME LOGIC AS OTHER TEMPLATES
  const selectedList =
    localSelectedListOverride ??
    viewSelectedList ?? // ✅ ADD THIS - was missing!
    externalSelectedList ??
    selectedPropertyList ??
    property?.propertyListType?.list_name ??
    "Just Listed";

  // Combine prop lists with local lists, provide defaults if empty
  const allLists = mergePropertyListsWithCurrent(
    localPropertyLists.length > 0
      ? localPropertyLists
      : [
        { id: 1, list_name: "Just Listed" },
        { id: 2, list_name: "Priced to Sell" },
        { id: 3, list_name: "Open House" },
        { id: 4, list_name: "New Price" },
      ],
    property?.propertyListType
  );

  const listSelectValue =
    resolvePropertyListName(
      selectedList,
      property?.propertyListType?.list_name
    ) || "Just Listed";

  // Custom fields editor functions
  const addCustomField7 = () => {
    if (readOnly) return;
    setCustomFields7((p) => [...p, { key: "", value: "" }]);
  };

  const updateCustomField7 = (i: number, field: "key" | "value", v: string) => {
    if (readOnly) return;
    setCustomFields7((p) => {
      const n = [...p];
      n[i] = { ...n[i], [field]: v };
      return n;
    });
  };

  const removeCustomField7 = (i: number) => {
    if (readOnly) return;
    setCustomFields7((p) => p.filter((_, idx) => idx !== i));
  };

  const saveCustomForTemplate7 = async () => {
    if (readOnly) return;
    try {
      if (typeof saveCustomTemplateData === "function") {
        await saveCustomTemplateData(
          "template7",
          customFields7.filter((f) => f.key || f.value)
        );
      } else {
        const token = localStorage.getItem("access_token");
        if (property?.propertyListType?.id) {
          await axiosInstance.patch(
            `${endpoints.propertylisttype}${property.propertyListType.id}/`,
            {
              custom_template_data: {
                ...(property.propertyListType.custom_template_data || {}),
                template7: customFields7,
              },
            },
            {
              headers: token ? { Authorization: `Bearer ${token}` } : undefined,
            }
          );
        }
      }
      setEditingCustom7(false);
      toast({ title: "Saved", description: "Custom fields saved" });
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: "Failed saving custom fields",
        variant: "destructive",
      });
    }
  };

  // === OPEN HOUSE LOGIC ===
  const [isOpenHouseConfirmed, setIsOpenHouseConfirmed] = useState<boolean>(
    property?.is_open_house ?? false
  );

  const handleOpenHouseChoice = async (confirmed: boolean) => {
    if (readOnly) return;
    if (!property?.id) return;

    setIsOpenHouseConfirmed(confirmed);

    try {
      await axiosInstance.patch(`${endpoints.properties}${property.id}/`, {
        is_open_house: confirmed,
      });

      toast({
        title: confirmed ? "Open House Active" : "Open House Disabled",
        description: confirmed
          ? "Special styling enabled"
          : "Open House mode turned off",
        variant: "default",
      });
    } catch (err) {
      toast({
        title: "Error",
        description: "Failed to save choice",
        variant: "destructive",
      });
      setIsOpenHouseConfirmed(!confirmed);
    }
  };

  useEffect(() => {
    setIsOpenHouseConfirmed(property?.is_open_house ?? false);
  }, [property?.is_open_house]);

  // Measure image container pixel dimensions for resolution suggestions
  useEffect(() => {
    const measure = () => {
      try {
        if (heroContainerRef.current) {
          const w = Math.round(heroContainerRef.current.clientWidth);
          const h = Math.round(heroContainerRef.current.clientHeight);
          setBoxHeroRes(`${w}×${h}`);
        }
        if (secondaryContainerRef.current) {
          const w2 = Math.round(secondaryContainerRef.current.clientWidth);
          const h2 = Math.round(secondaryContainerRef.current.clientHeight);
          setBoxSecondaryRes(`${w2}×${h2}`);
        }
      } catch {
        // ignore
      }
    };

    measure();
    const onResize = () => measure();
    window.addEventListener("resize", onResize);

    let ro: ResizeObserver | null = null;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => measure());
      if (heroContainerRef.current) ro.observe(heroContainerRef.current);
      if (secondaryContainerRef.current) ro.observe(secondaryContainerRef.current);
    }

    return () => {
      window.removeEventListener("resize", onResize);
      if (ro) ro.disconnect();
    };
  }, []);

  // === SAVE CUSTOM LIST - CREATES NEW PropertyListType IN DATABASE ===
  const saveCustomList = async () => {
    if (readOnly) return;

    const trimmedName = customListName.trim();

    if (!trimmedName) {
      toast({
        title: "Error",
        description: "Please enter a name for your custom tag",
        variant: "destructive",
      });
      return;
    }

    if (!property?.id) {
      toast({
        title: "Error",
        description: "No property selected. Please select a property first.",
        variant: "destructive",
      });
      return;
    }

    const existingList = localPropertyLists.find(
      (l) => l.list_name.toLowerCase() === trimmedName.toLowerCase()
    );

    if (existingList) {
      setLocalSelectedListOverride(existingList.list_name);
      updatePropertyList(existingList.list_name, existingList.id);
      setIsCustomList(false);
      setCustomListName("");

      toast({
        title: "List Selected",
        description: `"${existingList.list_name}" already exists and has been selected`,
      });
      return;
    }

    setIsSavingCustomList(true);

    try {
      const token = localStorage.getItem("access_token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};

      const createResponse = await axiosInstance.post(
        endpoints.propertylisttype,
        { list_name: trimmedName },
        { headers }
      );

      const newListType: PropertyList = {
        id: createResponse.data.id,
        list_name: createResponse.data.list_name,
      };

      setLocalSelectedListOverride(newListType.list_name);

      setLocalPropertyLists((prev) => {
        const exists = prev.some((l) => l.id === newListType.id);
        if (exists) return prev;
        return [...prev, newListType];
      });

      setIsCustomList(false);
      setCustomListName("");

      onPropertyListCreated?.(newListType);
      // Pass the freshly created id explicitly — propertyLists in the parent may not
      // have this brand-new entry yet, so a name-only lookup there could miss it.
      updatePropertyList(newListType.list_name, newListType.id);

      toast({
        title: "Custom Tag Created",
        description: `"${newListType.list_name}" has been created and selected`,
      });
    } catch (err: any) {
      console.error("Failed to create custom list:", err);

      const errorMessage =
        err?.response?.data?.list_name?.[0] ||
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        "Failed to create custom tag";

      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSavingCustomList(false);
    }
  };

  const cancelCustomList = () => {
    setIsCustomList(false);
    setCustomListName("");
  };

  // ✅ Handle existing list selection with immediate UI update
  const handleListSelection = (value: string) => {
    if (readOnly) return;

    if (value === "__custom__") {
      setIsCustomList(true);
      setCustomListName("");
      return;
    }

    setLocalSelectedListOverride(value);
    // Persistence (the PATCH request) happens once, upstream in the parent's
    // onPropertyListChange handler — see updatePropertyList in TemplateBase.
    updatePropertyList(value);
  };

  // === Image Upload Logic ===
  const handleFileChange = async (
    e: React.ChangeEvent<HTMLInputElement>,
    field: "heroImage" | "photo2" | "photo3" | "photo4"
  ) => {
    if (readOnly) return;

    const file = e.target.files?.[0];
    if (!file) return;

    if (field !== "heroImage") e.target.value = "";

    if (!file.type.startsWith("image/")) {
      toast({
        title: "Invalid File",
        description: "Please upload an image",
        variant: "destructive",
      });
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast({
        title: "File Too Large",
        description: "Max 10 MB",
        variant: "destructive",
      });
      return;
    }

    const reader = new FileReader();
    reader.onload = async () => {
      const dataUrl = reader.result as string;

      // Validate uploaded image resolution against the container box resolution (if available)
      try {
        const imgEl: HTMLImageElement = await new Promise((resolve, reject) => {
          const tmp = new Image();
          tmp.onload = () => resolve(tmp);
          tmp.onerror = reject;
          tmp.src = dataUrl;
        });

        const w = imgEl.naturalWidth;
        const h = imgEl.naturalHeight;

        if (field === "heroImage") {
          if (boxHeroRes) {
            const parts = boxHeroRes.split("×").map((s) => Number(s));
            if (parts.length === 2) {
              const [rw, rh] = parts;
              if (w !== rw || h !== rh) {
                toast({
                  title: "Resolution Suggestion",
                  description: `Uploaded image is ${w}×${h}. Recommended: ${boxHeroRes}. If possible, upload this resolution for best results.`,
                  variant: "default",
                });
              }
            }
          }
        } else {
          if (boxSecondaryRes) {
            const parts = boxSecondaryRes.split("×").map((s) => Number(s));
            if (parts.length === 2) {
              const [rw, rh] = parts;
              if (w !== rw || h !== rh) {
                toast({
                  title: "Resolution Suggestion",
                  description: `Uploaded image is ${w}×${h}. Recommended: ${boxSecondaryRes}. If possible, upload this resolution for best results.`,
                  variant: "default",
                });
              }
            }
          }
        }

        // Show preview immediately after validation
        setFlyerData((prev) => ({ ...prev, [field]: dataUrl }));
        if (field === "heroImage") {
          // This freshly-picked photo becomes the new "original" for this
          // session immediately, so Edit Image always has an untouched source
          // to re-crop from, right from this point on. Mirrors Template1.tsx.
          setOriginalHeroImage(dataUrl);
        }
      } catch (err) {
        toast({
          title: "Image Error",
          description: "Failed to read uploaded image. Please try another file.",
          variant: "destructive",
        });
        return;
      }

      try {
        const token = localStorage.getItem("access_token");
        const formData = new FormData();
        formData.append("image", file);

        if (field === "heroImage") {
          // Persist this same freshly-picked photo as the untouched
          // "original" separately from the displayed image, so Edit Image
          // can always re-crop from the full source later instead of
          // re-cropping an already-cropped result. Piggybacks on the PATCH
          // already being sent here so there's no extra request.
          formData.append("original_image", file);

          onDirtyChange?.(true);
          onChange?.({ heroImage: dataUrl });

          await axiosInstance.patch(
            `${endpoints.properties}${property?.id}/`,
            formData,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                "Content-Type": "multipart/form-data",
              },
            }
          );
          toast({ title: "Success", description: "Main photo updated" });
          onDirtyChange?.(false);
        } else {
          const res = await axiosInstance.post(
            endpoints.mls_uploads,
            formData,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
                "Content-Type": "multipart/form-data",
              },
            }
          );
          if (res?.data?.url) {
            updatePhotoAtIndex(
              res.data.url,
              field as "photo2" | "photo3" | "photo4"
            );
            toast({ title: "Success", description: "Photo uploaded", duration: 1000 });
          }
        }
      } catch (err) {
        toast({
          title: "Error",
          description: "Upload failed",
          variant: "destructive",
          duration: 1000,
        });
      }
    };
    reader.readAsDataURL(file);
  };

  // ─── Edit Image: reposition/zoom the ORIGINAL hero photo (no file picker) ──
  const EDIT_CROP_W = 430;
  const EDIT_CROP_H = 220; // matches the ~860x440 hero box aspect ratio

  const openEditImage = async () => {
    if (readOnly || !flyerData.heroImage) return;

    let originalSource = originalHeroImage;

    // This component may remount when the hero image changes, resetting local
    // state — so an empty originalHeroImage here doesn't necessarily mean
    // "never captured". Check the server (unaffected by remounts) before
    // assuming there isn't one and falling back to the current display.
    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_image || "";
      } catch (err) {
        console.error("Failed to check for existing original image:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet (new property, or MLS import). Self-heal
      // from the current image so this and every future edit has a stable,
      // full source to re-crop from instead of the current display.
      originalSource = flyerData.heroImage;
      if (property?.id && !flyerData.heroImage.startsWith("data:")) {
        const token = localStorage.getItem("access_token");
        axiosInstance
          .patch(
            `${endpoints.properties}${property.id}/`,
            { original_image: flyerData.heroImage },
            { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
          )
          .catch((err) => {
            console.error("Failed to seed original image:", err);
          });
      }
    }

    setOriginalHeroImage(originalSource);
    setCropModal({ open: true, imageUrl: getEditableImageUrl(originalSource) });
  };

  const handleEditImageConfirm = (croppedDataUrl: string) => {
    setFlyerData((prev) => ({ ...prev, heroImage: croppedDataUrl }));
    onDirtyChange?.(true);
    onChange?.({ heroImage: croppedDataUrl });
    setCropModal({ open: false, imageUrl: "" });
  };

  const handleEditImageCancel = () => {
    setCropModal({ open: false, imageUrl: "" });
  };

  // ─── Edit Image for secondary/gallery photos (same non-destructive pattern
  // as the hero photo above, reusing the shared updatePhotoAtIndex/
  // uploadCroppedPhoto helpers from TemplateBase so original-capture stays
  // centralized) ──
  const SECONDARY_CROP_W = 260;
  const SECONDARY_CROP_H = 230; // matches the fixed 260x230 secondary-photo box

  const [secondaryCropModal, setSecondaryCropModal] = useState<{
    open: boolean;
    imageUrl: string;
    field: "photo2" | "photo3" | "photo4" | null;
  }>({ open: false, imageUrl: "", field: null });

  const openEditSecondaryPhoto = async (
    field: "photo2" | "photo3" | "photo4",
    currentSrc: string
  ) => {
    if (readOnly || !currentSrc) return;

    // Keyed by slot name ("photo2"/"photo3"/"photo4"), not array position —
    // see TemplateBase.tsx's updatePhotoAtIndex for why.
    let originalSource = (flyerData.original_photos && flyerData.original_photos[field]) || "";

    if (!originalSource && property?.id) {
      try {
        const token = localStorage.getItem("access_token");
        const res = await axiosInstance.get(
          `${endpoints.properties}${property.id}/`,
          { headers: token ? { Authorization: `Bearer ${token}` } : undefined }
        );
        originalSource = res?.data?.original_photos?.[field] || "";
      } catch (err) {
        console.error("Failed to check for existing original photo:", err);
      }
    }

    if (!originalSource) {
      // Truly never captured yet. Self-heal by re-saving the current photo
      // through updatePhotoAtIndex (captureOriginal defaults to true), so
      // this and every future edit has a stable, full source to re-crop
      // from instead of the current display.
      originalSource = currentSrc;
      if (!currentSrc.startsWith("data:")) {
        updatePhotoAtIndex(currentSrc, field).catch((err) => {
          console.error("Failed to seed original photo:", err);
        });
      }
    }

    setSecondaryCropModal({
      open: true,
      imageUrl: getEditableImageUrl(originalSource),
      field,
    });
  };

  const handleSecondaryEditConfirm = async (croppedDataUrl: string) => {
    const field = secondaryCropModal.field;
    if (!field) return;
    try {
      const newUrl = await uploadCroppedPhoto(croppedDataUrl);
      await updatePhotoAtIndex(newUrl, field, { captureOriginal: false });
      toast({ title: "Success", description: "Photo updated" });
    } catch (err) {
      console.error("Failed to save edited photo:", err);
      toast({
        title: "Error",
        description: "Failed to save edited photo",
        variant: "destructive",
      });
    } finally {
      setSecondaryCropModal({ open: false, imageUrl: "", field: null });
    }
  };

  const handleSecondaryEditCancel = () => {
    setSecondaryCropModal({ open: false, imageUrl: "", field: null });
  };

  const handleRemovePhoto = (
    field: "heroImage" | "photo2" | "photo3" | "photo4"
  ) => {
    if (readOnly) return;
    setFlyerData((prev) => ({
      ...prev,
      [field]: field === "heroImage" ? "" : undefined,
    }));
    onDirtyChange?.(true);
    onChange?.({ [field]: field === "heroImage" ? "" : undefined });
    toast({ title: "Removed", description: "Photo removed" });
  };

  return (

    <div style={{ overflowX: "clip", overflowY: "hidden" }}>
      <div
        style={{
          WebkitOverflowScrolling: "touch",
          transform: "rotateX(180deg)",
          overflowX: "auto",
          overflowY: "hidden",
          width: "100%",
        }}
      >
        <div
          className="bg-gradient-to-br from-[#f8fafc] to-[#f1f5f9] m-auto font-arial relative border rounded-lg shadow-none overflow-hidden"
          style={{ minWidth: "860px", width: "860px", transform: "rotateX(180deg)" }}
        >
          {/* Custom Fields Editor Modal */}
          {editingCustom7 && !readOnly && (
            <div className="absolute right-6 top-20 z-50 bg-white p-4 rounded-lg shadow-xl w-80 border border-gray-200">
              <div className="flex items-center justify-between mb-3">
                <strong className="text-gray-900">Custom Fields</strong>
                <div className="flex gap-2">
                  <button
                    onClick={addCustomField7}
                    className="text-sm text-blue-600 hover:text-blue-800"
                  >
                    Add
                  </button>
                  <button
                    onClick={() => setEditingCustom7(false)}
                    className="text-sm text-gray-600 hover:text-gray-800"
                  >
                    Close
                  </button>
                </div>
              </div>
              <div className="max-h-48 overflow-auto">
                {customFields7.map((f, idx) => (
                  <div key={idx} className="flex gap-2 items-center mb-2">
                    <input
                      value={f.key}
                      onChange={(e) =>
                        updateCustomField7(idx, "key", e.target.value)
                      }
                      placeholder="Key"
                      className="border border-gray-300 px-2 py-1 flex-1 text-sm rounded"
                    />
                    <input
                      value={f.value}
                      onChange={(e) =>
                        updateCustomField7(idx, "value", e.target.value)
                      }
                      placeholder="Value"
                      className="border border-gray-300 px-2 py-1 flex-1 text-sm rounded"
                    />
                    <button
                      onClick={() => removeCustomField7(idx)}
                      className="text-red-600 hover:text-red-800"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="mt-3 flex justify-end">
                <button
                  onClick={saveCustomForTemplate7}
                  className="bg-green-600 text-white px-4 py-1.5 rounded text-sm hover:bg-green-700"
                >
                  Save
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="px-8 pt-6 pb-4 flex flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-red-500 to-orange-500 flex items-center justify-center shadow-lg">
                <Zap className="h-6 w-6 text-white" />
              </div>
              <span className="text-lg font-bold text-gray-800">
                {agentData?.company || "Premier Realty Group"}
              </span>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              <Tag className="h-5 w-5 text-red-500" />

              {/* Property List Selector */}
              {/* ✅ ADD READONLY CHECK - same as other templates */}
              {readOnly ? (
                <div className="bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-800 px-4 py-2 rounded-xl font-medium">
                  {listSelectValue || "Just Listed"}
                </div>
              ) : isCustomList ? (
                <div className="flex items-center gap-2 bg-white/90 backdrop-blur-sm rounded-xl px-4 py-2 border border-gray-200">
                  <input
                    type="text"
                    value={customListName}
                    onChange={(e) => setCustomListName(e.target.value.slice(0, 20))}
                    placeholder="Custom tag (max 20 chars)"
                    maxLength={20}
                    className="bg-transparent text-gray-800 placeholder-gray-400 px-2 py-1 text-sm w-28 md:w-36 outline-none border-none"
                    autoFocus
                    disabled={isSavingCustomList}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !isSavingCustomList) {
                        e.preventDefault();
                        saveCustomList();
                      }
                      if (e.key === "Escape") {
                        cancelCustomList();
                      }
                    }}
                  />
                  <button
                    onClick={saveCustomList}
                    disabled={!customListName.trim() || isSavingCustomList}
                    className="p-1.5 bg-red-100 rounded-full hover:bg-red-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    title="Save custom tag"
                  >
                    {isSavingCustomList ? (
                      <Loader2 className="h-4 w-4 text-red-600 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 text-red-600" />
                    )}
                  </button>
                  <button
                    onClick={cancelCustomList}
                    disabled={isSavingCustomList}
                    className="p-1.5 bg-gray-100 rounded-full hover:bg-gray-200 disabled:opacity-50 transition-colors"
                    title="Cancel"
                  >
                    <X className="h-4 w-4 text-gray-600" />
                  </button>
                </div>
              ) : (
                <select
                  value={listSelectValue}
                  onChange={(e) => handleListSelection(e.target.value)}
                  className="bg-white/90 backdrop-blur-sm border border-gray-200 text-gray-800 px-4 py-2 rounded-xl font-medium focus:outline-none focus:ring-2 focus:ring-red-400"
                >
                  <option value="">-- Select --</option>
                  {allLists
                    .filter((list) => list.list_name !== "Open House")
                    .map((l) => (
                      <option key={l.id} value={l.list_name}>
                        {l.list_name}
                      </option>
                    ))}
                  <option value="__custom__" className="font-medium">
                    + Add Custom Tag
                  </option>
                </select>
              )}

              {/* OPEN HOUSE BADGE */}
              {/* {isOpenHouseConfirmed && (
            <div className="flex items-center gap-2 px-4 py-2 bg-green-500 rounded-xl shadow">
              <span className="text-xs md:text-sm font-bold text-white">
                OPEN HOUSE
              </span>
            </div>
          )} */}
            </div>
          </div>

          {/* Hero Image + Price + Address */}
          <div className="px-8 pb-6">
            <div ref={heroContainerRef} className="relative rounded-2xl overflow-hidden shadow-2xl group h-[260px] sm:h-[320px] md:h-80 lg:h-96"
              style={{
                height: '440px',
                minHeight: '440px',
                maxHeight: '440px',
                width: '100%',   // 100% of the 760px parent — correct
              }}>
              {!readOnly && (
                <div className="absolute bottom-3 right-3 z-40 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                  <div className="bg-white/90 text-gray-800 text-xs rounded-xl px-3 py-2 border border-gray-200 max-w-[320px]">
                    {boxHeroRes ? (
                      <div className="leading-snug">
                        <div className="font-semibold">Required image resolution: {boxHeroRes}</div>
                        <div className="mt-1 text-[11px]">Please upload exactly this resolution.</div>
                      </div>
                    ) : (
                      <div className="leading-snug">{RESOLUTION_HINT}</div>
                    )}
                  </div>
                </div>
              )}
              {isOpenHouseConfirmed && (
                <div className="absolute top-4 right-4 md:top-6 md:right-0 z-20" style={{ marginRight: '-10px' }}>
                  <div className="relative w-16 sm:w-24 md:w-32">
                    <img
                      src={openHouseBadge}
                      alt="Open House"
                      className="w-full drop-shadow-xl"
                    />
                    <span
                      className="absolute inset-0 flex items-center justify-center
                   text-white font-extrabold text-[10px] sm:text-xs md:text-sm
                   tracking-wide text-center pointer-events-none"
                    >
                      OPEN
                      <br />
                      HOUSE
                    </span>
                  </div>
                </div>
              )}

              {flyerData.heroImage ? (
                <img
                  src={flyerData.heroImage}
                  alt="Main"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
              ) : (
                <div className="bg-gray-200 w-full h-full flex items-center justify-center">
                  <ImageIcon className="h-20 w-20 text-gray-400" />
                </div>
              )}

              {/* ✅ Only show hover overlay when NOT readOnly */}
              {!readOnly && (
                <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-4">
                  <Button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-white/20 backdrop-blur text-white hover:bg-white/30"
                  >
                    <Upload className="h-4 w-4 mr-2" /> Change
                  </Button>
                  <Button
                    onClick={openEditImage}
                    className="bg-white/20 backdrop-blur text-white hover:bg-white/30"
                  >
                    <Crop className="h-4 w-4 mr-2" /> Edit
                  </Button>
                </div>
              )}

              {/* ✅ Only render file input when NOT readOnly */}
              {!readOnly && (
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleFileChange(e, "heroImage")}
                />
              )}
              {!readOnly && (
                <ImageCropModal
                  open={cropModal.open}
                  imageUrl={cropModal.imageUrl}
                  title="Adjust Property Photo"
                  outputSize={700}
                  cropW={EDIT_CROP_W}
                  cropH={EDIT_CROP_H}
                  onConfirm={handleEditImageConfirm}
                  onCancel={handleEditImageCancel}
                />
              )}

              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 to-transparent p-4 sm:p-6 md:p-8">
                <div className="flex items-baseline gap-2">
                  <span className="text-white/80 text-2xl">$</span>
                  {/* ✅ Price - readOnly conditional */}
                  {readOnly ? (
                    <p className="bg-transparent text-2xl sm:text-4xl md:text-5xl font-bold text-white border-none outline-none w-full leading-none">
                      {flyerData.price
                        ? Number(flyerData.price).toLocaleString()
                        : "0"}
                    </p>
                  ) : (
                    <input
                      type="text"
                      value={
                        flyerData.price
                          ? Number(flyerData.price).toLocaleString()
                          : ""
                      }
                      onChange={(e) => {
                        const num = e.target.value.replace(/\D/g, "");
                        handleInputChange("price", num ? Number(num) : null);
                      }}
                      className="bg-transparent text-5xl font-bold text-white border-none outline-none w-full"
                      placeholder="425,000"
                    />
                  )}
                </div>
                <div className="flex items-start gap-2 sm:gap-3 mt-2 sm:mt-3">
                  <MapPin className="h-5 w-5 text-white/70 flex-shrink-0" />
                  {/* ✅ Address - readOnly conditional */}
                  {readOnly ? (
                    <p className="text-white/90 text-sm sm:text-base md:text-lg leading-snug break-words">
                      {flyerData.address || "No address provided"}
                    </p>
                  ) : (
                    <input
                      type="text"
                      value={flyerData.address}
                      onChange={(e) => handleInputChange("address", e.target.value)}
                      className="bg-transparent text-white/90 text-sm sm:text-base md:text-lg border-none outline-none w-full leading-snug"
                      placeholder="123 Luxury Lane, Beverly Hills, CA"
                    />
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Secondary Photos - FIXED */}
          <div className="px-8 pb-6">
            <div className="grid grid-cols-3 gap-2 sm:gap-4">
              {[
                { ref: photo2Ref, field: "photo2" as const, index: 0 },
                { ref: photo3Ref, field: "photo3" as const, index: 1 },
                { ref: photo4Ref, field: "photo4" as const, index: 2 },
              ].map(({ ref, field, index }) => {
                // ✅ FIXED: Check multiple sources for photos
                const getPhotoSrc = (): string => {
                  // 1. Check flyerData field directly (e.g., flyerData.photo2)
                  const fieldValue = flyerData[field];
                  if (
                    fieldValue &&
                    typeof fieldValue === "string" &&
                    fieldValue.trim() !== ""
                  ) {
                    return fieldValue;
                  }

                  // 2. Check flyerData.photos array
                  if (Array.isArray(flyerData.photos) && flyerData.photos[index]) {
                    const photoFromArray = flyerData.photos[index];
                    if (
                      photoFromArray &&
                      typeof photoFromArray === "string" &&
                      photoFromArray.trim() !== ""
                    ) {
                      return photoFromArray;
                    }
                  }

                  // 3. Check property.photos array as fallback
                  if (
                    property &&
                    Array.isArray(property.photos) &&
                    property.photos[index]
                  ) {
                    const propertyPhoto = property.photos[index];
                    if (
                      propertyPhoto &&
                      typeof propertyPhoto === "string" &&
                      propertyPhoto.trim() !== ""
                    ) {
                      return propertyPhoto;
                    }
                  }

                  return "";
                };

                const src = getPhotoSrc();

                return (
                  <div
                    key={field}
                    ref={index === 0 ? secondaryContainerRef : undefined}
                    className="relative rounded-xl overflow-hidden shadow-lg group "
                    style={{
                      height: '230px',
                      minHeight: '230px',
                      maxHeight: '230px',
                      width: '260px',
                      minWidth: '260px',
                      maxWidth: '260px',
                    }}>
                    {/* Resolution hint for secondary images */}
                    {!readOnly && (
                      <div className="absolute bottom-2 left-2 z-30 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none">
                        <div className="bg-white/90 text-gray-800 text-xs rounded-lg px-2 py-1 border border-gray-200">
                          {boxSecondaryRes
                            ? `Required image resolution: ${boxSecondaryRes}`
                            : RESOLUTION_HINT}
                        </div>
                      </div>
                    )}
                    {src ? (
                      <img
                        src={src}
                        alt={`Property photo ${index + 2}`}
                        className="w-full h-full object-cover object-center"
                        onError={(e) => {
                          console.error(`Failed to load image: ${src}`);
                          // Hide broken image and show placeholder
                          const target = e.target as HTMLImageElement;
                          target.style.display = "none";
                          // Show placeholder
                          const placeholder =
                            target.nextElementSibling as HTMLElement;
                          if (placeholder) {
                            placeholder.style.display = "flex";
                          }
                        }}
                      />
                    ) : null}

                    {/* Placeholder - shown when no image or on error */}
                    <div
                      className={`bg-gray-200 h-full flex items-center justify-center ${src ? "hidden" : ""
                        }`}
                    >
                      <ImageIcon className="h-12 w-12 text-gray-400" />
                    </div>

                    {/* ✅ Only show hover overlay when NOT readOnly */}
                    {!readOnly && (
                      <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-3">
                        <Button
                          size="sm"
                          onClick={() => ref.current?.click()}
                          className="bg-white/20 backdrop-blur text-white"
                        >
                          <Upload className="h-4 w-4 mr-1" />{" "}
                          {src ? "Change" : "Upload"}
                        </Button>
                        {src && (
                          <Button
                            size="sm"
                            onClick={() => openEditSecondaryPhoto(field, src)}
                            className="bg-white/20 backdrop-blur text-white"
                          >
                            <Crop className="h-4 w-4 mr-1" /> Edit
                          </Button>
                        )}
                      </div>
                    )}

                    {/* ✅ Only render file input when NOT readOnly */}
                    {!readOnly && (
                      <input
                        ref={ref}
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleFileChange(e, field)}
                      />
                    )}
                  </div>
                );
              })}
            </div>
            {!readOnly && (
              <ImageCropModal
                open={secondaryCropModal.open}
                imageUrl={secondaryCropModal.imageUrl}
                title="Adjust Property Photo"
                outputSize={700}
                cropW={SECONDARY_CROP_W}
                cropH={SECONDARY_CROP_H}
                onConfirm={handleSecondaryEditConfirm}
                onCancel={handleSecondaryEditCancel}
              />
            )}
          </div>

          {/* Quick Stats */}
          <div className="px-8 pb-6">
            <div className="bg-white/80 backdrop-blur rounded-2xl shadow-md border border-gray-200 p-4 sm:p-6">
              <div className="grid grid-cols-3 gap-2 sm:gap-6 md:gap-8 text-center">
                {[
                  {
                    icon: Bed,
                    field: "bedrooms" as const,
                    label: "Beds",
                    color: "text-red-500",
                  },
                  {
                    icon: Bath,
                    field: "bathrooms" as const,
                    label: "Baths",
                    color: "text-orange-500",
                  },
                  {
                    icon: Square,
                    field: "sqft" as const,
                    label: "Sq Ft",
                    color: "text-purple-600",
                  },
                ].map(({ icon: Icon, field, label, color }) => {
                  const value = flyerData[field] as number | null;
                  return (
                    <div key={field}>
                      <Icon className={`h-5 w-5 sm:h-7 sm:w-7 md:h-8 md:w-8 mx-auto mb-1 sm:mb-2 ${color}`} />
                      {/* ✅ Stats - readOnly conditional */}
                      {readOnly ? (
                        <p className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 text-center">
                          {value ?? 0}
                        </p>
                      ) : (
                        <input
                          type="number"
                          min="0"
                          value={value ?? ""}
                          onChange={(e) =>
                            handleInputChange(
                              field,
                              e.target.value ? Number(e.target.value) : null
                            )
                          }
                          className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-800 bg-transparent text-center w-full border-none outline-none [-moz-appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          placeholder="0"
                        />
                      )}
                      <p className="text-[11px] sm:text-sm text-gray-600 mt-1">{label}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Description */}
          <div className="px-8 pb-6">
            {/* ✅ Description - readOnly conditional */}
            {readOnly ? (
              <div className="w-full bg-white/70 backdrop-blur border border-gray-300 rounded-xl p-5 text-gray-700">
                {flyerData.description || "No description provided."}
              </div>
            ) : (
              <textarea
                value={flyerData.description}
                onChange={(e) => handleInputChange("description", e.target.value)}
                rows={4}
                className="w-full bg-white/70 backdrop-blur border border-gray-300 rounded-xl p-5 text-gray-700 focus:outline-none focus:ring-2 focus:ring-red-400 resize-none"
                placeholder="Stunning modern residence with breathtaking views..."
              />
            )}
          </div>

          {/* Key Features */}
          <div className="px-8 pb-6">
            <h3 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-3">
              <Zap className="h-7 w-7 text-red-500" />
              <span>Key Features</span>
              {!readOnly && (
                <span className="text-sm font-normal text-gray-500">
                  ({flyerData.features.length}/10)
                </span>
              )}
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {flyerData.features.map((f, i) => (
                <div
                  key={f.id ?? i}
                  className="flex items-center gap-3 bg-white/80 backdrop-blur rounded-xl 
                   p-4 border border-gray-200 hover:border-red-300 
                   transition-all group min-h-[56px] w-full"
                >
                  <div className="w-3 h-3 rounded-full bg-gradient-to-r from-red-500 to-orange-500 flex-shrink-0" />

                  {/* ✅ Features - readOnly conditional */}
                  {readOnly ? (
                    <span className="flex-1 text-gray-800 font-medium">
                      {f.feature_name || "Feature"}
                    </span>
                  ) : (
                    <>
                      <input
                        type="text"
                        value={f.feature_name}
                        onChange={(e) => handleFeatureChange(i, e.target.value)}
                        onBlur={() => handleFeatureBlur(i)}
                        className="flex-1 bg-transparent border-none outline-none 
                         text-gray-800 font-medium min-w-0"
                        placeholder="e.g., Gourmet Kitchen"
                        maxLength={30}
                      />

                      <button
                        onMouseDown={(e) => {
                          e.preventDefault();
                          handleFeatureDelete(i);
                        }}
                        onTouchStart={(e) => {
                          e.preventDefault();
                          handleFeatureDelete(i);
                        }}
                        className="text-red-500 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </>
                  )}
                </div>
              ))}

              {/* ✅ Add Feature Button - Only show when NOT readOnly */}
              {!readOnly && flyerData.features.length < 10 && (
                <button
                  onClick={addFeature}
                  className="flex items-center justify-center bg-white/80 min-h-[56px] w-full 
                   border border-red-300 border-dashed rounded-xl 
                   hover:border-red-500 text-red-500 font-medium"
                >
                  + Add Feature
                </button>
              )}

              {!readOnly && flyerData.features.length >= 10 && (
                <p className="col-span-full text-center text-sm text-gray-500 italic">
                  Maximum of 10 features reached
                </p>
              )}
            </div>
          </div>

          {/* Agent Section */}
          <div className="px-6 pb-8">
            <div className="bg-gradient-to-r from-gray-900 to-gray-800 rounded-2xl p-3 text-white">
              <div className="flex flex-row items-center gap-6">

                {/* Agent Avatar - LEFT */}
                <div className="w-[150px] h-[180px] rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">
                  {agentData?.avatar ? (
                    <img src={agentData.avatar} alt="Agent" className="w-full h-full object-cover" />
                  ) : (
                    <ImageIcon className="w-14 h-14 text-gray-500" />
                  )}
                </div>

                {/* Agent Info - CENTER */}
                <div className="flex-1 min-w-0 flex flex-col justify-center items-center">

                  {/* Agent Name */}
                  <p className="text-3xl font-bold text-white">
                    {flyerData.agentName || agentData?.name || "Agent Name"}
                  </p>

                  {/* Contact Info */}
                  <div className="flex flex-col gap-3 mt-2 w-full items-center justify-center">

                    {/* Phone */}
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <Phone className="h-5 w-5 text-red-400 flex-shrink-0" />
                      <span className="text-white/90">
                        {flyerData.agentPhone || agentData?.phone || "N/A"}
                      </span>
                    </div>

                    {/* Email */}
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <Mail className="h-5 w-5 text-red-400 underline flex-shrink-0" />
                      <span className="text-white/80 underline">
                        {/* {flyerData.agentEmail || agentData?.email || "N/A"} */}
                          Click here to email Me
                      </span>
                    </div>

                    {/* Company Name */}
                    <div className="inline-flex items-center gap-1.5 mx-auto">
                      <Building2 className="h-5 w-5 text-red-400 flex-shrink-0" />
                      <span className="text-white/80">
                        {flyerData.companyName || agentData?.company || "N/A"}
                      </span>
                    </div>

                    {/* License Number */}
                    {agentData?.license_number && (
                      <div className="inline-flex items-center gap-1.5 mx-auto">
                        <IdCard className="h-5 w-5 text-red-400 flex-shrink-0" />
                        <span className="text-white/80">
                          License #: {agentData.license_number}
                        </span>
                      </div>
                    )}

                  </div>
                </div>

                {/* Company Logo - RIGHT */}
                <div className="w-[180px] h-[180px] rounded-xl overflow-hidden flex items-center justify-center flex-shrink-0">
                  {agentData?.company_logo ? (
                    <img src={agentData.company_logo} alt="Company Logo" className="w-full h-full object-cover rounded-xl" />
                  ) : (
                    <span className="text-sm text-gray-400 text-center px-1">Company Logo</span>
                  )}
                </div>

              </div>
            </div>
          </div>

          {/* Custom fields (read-only display) from PropertyListType */}
          {property?.propertyListType?.custom_template_data?.template7 &&
            Array.isArray(
              property.propertyListType.custom_template_data.template7
            ) &&
            property.propertyListType.custom_template_data.template7.length > 0 && (
              <div className="px-8 pb-6">
                <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
                  <h4 className="font-semibold text-sm text-gray-800 mb-2">
                    Custom Fields
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm text-gray-700">
                    {property.propertyListType.custom_template_data.template7.map(
                      (f: any, i: number) => (
                        <div key={i} className="flex justify-between">
                          <span className="font-medium">{f.key}</span>
                          <span>{f.value}</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              </div>
            )}
        </div>
      </div>
    </div>
  );
};

export default Template7;
